import { fieldText, parseDraft } from "@/lib/record-fields";
import type { LeadCard, LeadCardCampaign, LeadFields, RecordField } from "@/lib/types";

/**
 * Another person of the same accident, added on the call (ACL-510): a passenger, a child in the back
 * seat. The rep types the person's name and date of birth; the facts of the crash come from the
 * record they are added from, and the agreement follows the date of birth, as core-api's contract
 * service decides it (`contract_service.is_minor`). Pure.
 */

/**
 * The facts of the crash itself, the same for everyone in it, in the lead form's keys
 * (`form_seed.py` in platform). Only these are copied. A person's own answers (their injuries and
 * treatment, their insurance, a lawyer, the incident notes in the caller's words, the vehicle they
 * were in) are not, because they are the caller's and would stand on the new record as the
 * passenger's.
 */
export const ACCIDENT_FACT_KEYS: readonly string[] = [
  "date_of_incident",
  "city_of_accident",
  "mva_accident_state",
  "estimated_time_of_accident",
  "accident_location",
  "when_did_this_accident_happen2",
  "who_was_at_fault_for_the_accident",
  "policeincident_report_completed",
  "police_report_number",
  "independent_witnesses__video_footage",
  "at_fault_driver_info",
  "atfault_drivers_auto_insurance",
];

/** The facts every agreement needs (`REQUIRED_KEYS` of the contract service) that come from the crash. */
export const AGREEMENT_FACT_KEYS: readonly string[] = ["date_of_incident", "city_of_accident"];
export const DOB_KEY = "date_of_birth";
/** What a minor's agreement needs on top (`GUARDIAN_KEYS`). */
export const GUARDIAN_NAME_KEY = "name_parentlegal_guardian";
export const GUARDIAN_RELATION_KEY = "relationship_to_client";
/** Under this age on the day the agreement goes out, the firm's minor agreement does. */
export const ADULT_AGE = 18;
/** The day the contract service counts age on when the lead has no clock of its own (`FALLBACK_ZONE`). */
export const FALLBACK_ZONE = "Pacific/Honolulu";

/** Every field of the form, by key. */
export function fieldsByKey(fields: LeadFields | undefined): Map<string, RecordField> {
  return new Map((fields?.sections ?? []).flatMap((s) => s.fields).map((f) => [f.key, f]));
}

/** The reader may write the key on a record of this form: a field of its own the role edits. A record
 *  made from the same form gives the same answer, so the source's view stands for the new one's. */
export function writable(fields: LeadFields | undefined, key: string): boolean {
  const field = fieldsByKey(fields).get(key);
  return field !== undefined && field.can_edit && (field.storage === "value" || field.storage === "accident");
}

/** The facts of the crash the accident holds (`storage: accident`, ADR-221): shared by everyone in it,
 *  so a new record in the same accident reads them at once and nothing is copied. */
export function sharedFacts(fields: LeadFields | undefined): CopiedFact[] {
  return copiedFacts(fields).filter((fact) => fieldsByKey(fields).get(fact.key)?.storage === "accident");
}

export interface CopiedFact {
  key: string;
  title: string;
  /** As the record shows it. */
  text: string;
  /** As stored, sent back as it is. */
  value: unknown;
}

/** The facts of the crash the source record has a value for, that the reader may write, in the
 *  form's order: what the new person's record starts with. */
export function copiedFacts(fields: LeadFields | undefined): CopiedFact[] {
  const out: CopiedFact[] = [];
  for (const section of fields?.sections ?? []) {
    for (const field of section.fields) {
      if (!ACCIDENT_FACT_KEYS.includes(field.key) || !writable(fields, field.key)) continue;
      const text = fieldText(field);
      if (text === null) continue;
      out.push({ key: field.key, title: titleOf(field), text, value: field.value });
    }
  }
  return out;
}

/** The facts every agreement needs that the source record has none for, by title: the new record
 *  lacks them too, and Send contract will ask for them. */
export function missingFacts(fields: LeadFields | undefined): string[] {
  const copied = new Set(copiedFacts(fields).map((f) => f.key));
  const byKey = fieldsByKey(fields);
  return AGREEMENT_FACT_KEYS.filter((key) => !copied.has(key)).map((key) => {
    const field = byKey.get(key);
    return field ? titleOf(field) : key === "date_of_incident" ? "Date of accident" : "City of accident";
  });
}

/** A field's title as a rep reads it in a sentence: without the `*` GoHighLevel marks a required one with. */
export function titleOf(field: Pick<RecordField, "title">): string {
  return field.title.replace(/\s*\*+\s*$/, "") || field.title;
}

/** The date on a clock (`YYYY-MM-DD`), as the contract service reads "today" for the lead. */
export function todayIn(zone: string | null | undefined, now: Date = new Date()): string {
  const read = (tz: string) =>
    new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
  try {
    return read(zone || FALLBACK_ZONE);
  } catch {
    return read(FALLBACK_ZONE);
  }
}

/** Whole years from `born` to `today`, both `YYYY-MM-DD`; null for no date, a bad one or one still to come. */
export function ageOn(born: string | null | undefined, today: string): number | null {
  const birth = /^(\d{4})-(\d{2})-(\d{2})$/.exec(born ?? "");
  const now = /^(\d{4})-(\d{2})-(\d{2})$/.exec(today);
  if (!birth || !now) return null;
  const [by, bm, bd] = birth.slice(1).map(Number) as [number, number, number];
  const [ty, tm, td] = now.slice(1).map(Number) as [number, number, number];
  const real = new Date(Date.UTC(by, bm - 1, bd));
  if (real.getUTCMonth() !== bm - 1 || real.getUTCDate() !== bd) return null;
  if (born! > today) return null;
  return ty - by - (tm < bm || (tm === bm && td < bd) ? 1 : 0);
}

export type Agreement = { kind: "minor" | "adult"; age: number } | null;

/** Which agreement goes out for this date of birth today: the firm's minor one under 18. */
export function agreementFor(born: string | null | undefined, today: string): Agreement {
  const age = ageOn(born, today);
  if (age === null) return null;
  return { kind: age < ADULT_AGE ? "minor" : "adult", age };
}

export function agreementText(agreement: Agreement): string | null {
  if (!agreement) return null;
  const years = `${agreement.age} ${agreement.age === 1 ? "year" : "years"} old`;
  return agreement.kind === "minor"
    ? `Minor agreement (${years}): a parent or legal guardian signs it.`
    : `Adult agreement (${years}).`;
}

export interface PersonAnswers {
  dateOfBirth: string;
  guardianName: string;
  guardianRelation: string;
}

/** What the new record's fields are set to: the crash's facts, the date of birth, and for a minor the
 *  guardian. Only keys the reader may write; a value that does not parse is named in `problems`. */
export function personValues(
  fields: LeadFields | undefined,
  answers: PersonAnswers,
  agreement: Agreement,
): { values: Record<string, unknown>; problems: Record<string, string> } {
  const values: Record<string, unknown> = {};
  const problems: Record<string, string> = {};
  for (const fact of copiedFacts(fields)) values[fact.key] = fact.value;
  const byKey = fieldsByKey(fields);
  const typed: [string, string][] = [[DOB_KEY, answers.dateOfBirth]];
  if (agreement?.kind === "minor") {
    typed.push([GUARDIAN_NAME_KEY, answers.guardianName], [GUARDIAN_RELATION_KEY, answers.guardianRelation]);
  }
  for (const [key, draft] of typed) {
    const field = byKey.get(key);
    if (!field || !writable(fields, key) || !draft.trim()) continue;
    const parsed = parseDraft(field, draft);
    if (parsed.ok) values[key] = parsed.value;
    else problems[key] = parsed.problem;
  }
  return { values, problems };
}

/** The two numbers are one phone: E.164 against what a rep typed, `(310) 555-0142` or `+1 310…`. */
export function samePhone(a: string, b: string): boolean {
  const digits = (raw: string) => {
    const d = raw.replace(/\D/g, "");
    return d.length === 10 ? `1${d}` : d;
  };
  const left = digits(a);
  return left.length > 0 && left === digits(b);
}

/** As the record's own e-mail detail is checked (`record-fields.ts`): something before one `@`, a dot after
 *  it, no spaces, at most 254 characters. */
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const EMAIL_MAX = 254;

/** What is wrong with the e-mail typed for the new person, or null; empty is fine, Send contract asks for it. */
export function emailProblem(draft: string): string | null {
  const text = draft.trim();
  if (!text) return null;
  return text.length > EMAIL_MAX || !EMAIL_RE.test(text) ? "That does not look like an e-mail address" : null;
}

// --- "Add them to the dial list" (ADR-225) ---------------------------------------------------------

/** The list a party the rep adds or links may be called from: the campaign the rep is dialing for. */
export interface DialWith {
  campaignId: string;
  campaignName: string;
  /** The call the rep is on, when there is one: the row waits for its outcome. */
  callId: string | null;
}

/** A list row the lead is still on: not retired. */
export function liveRow(row: Pick<LeadCardCampaign, "retire_at">, now: Date = new Date()): boolean {
  return !row.retire_at || new Date(row.retire_at) > now;
}

/**
 * Which list the checkbox names. On the Phone page the call's line's campaign; on the record, the
 * campaign of the list the source record is on (its live row, the one it came in for first). With
 * neither there is no list to name, and no checkbox.
 */
export function dialWithFor(
  source: Pick<LeadCard, "campaigns" | "calling_campaign">,
  line: { campaign_id: string | null; campaign_name: string | null } | null | undefined,
  callId: string | null = null,
  now: Date = new Date(),
): DialWith | null {
  if (line?.campaign_id) {
    return { campaignId: line.campaign_id, campaignName: line.campaign_name ?? "this campaign", callId };
  }
  const rows = (source.campaigns ?? []).filter((row) => liveRow(row, now));
  const first = rows.find((row) => row.campaign_id === source.calling_campaign?.id) ?? rows[0];
  if (!first) return null;
  return { campaignId: first.campaign_id, campaignName: first.campaign_name ?? "this campaign", callId: null };
}

/** Whether the campaign is among the lists a record is on (`AccidentRecord.lists`, or a card's rows). */
export function onList(lists: readonly string[] | undefined, campaignId: string): boolean {
  return (lists ?? []).includes(campaignId);
}

/** The campaigns a card's live rows are on, as `AccidentRecord.lists` would say them. */
export function listsOf(card: Pick<LeadCard, "campaigns"> | undefined, now: Date = new Date()): string[] {
  return (card?.campaigns ?? []).filter((row) => liveRow(row, now)).map((row) => row.campaign_id);
}

/**
 * How the checkbox starts for someone new: unticked on the caller's own number (that number is
 * already dialed through the caller's record; a second row would ring it twice), ticked on a
 * number of their own.
 */
export function startsTicked(phone: string, sourcePhone: string): boolean {
  return !samePhone(phone, sourcePhone);
}
